// S7 オペレータコンソール（移行フェーズ2）。
//
// Cloudflare 版 public/ops/ の移植。SAKAMA の運用者専用で、テナント横断。
// 顧客テナント内の admin には見せない（他社のジョブが見えてしまうため）。
//
// 読み書きとも Callable Functions 経由。クライアントから Firestore を横断参照させない。
import { useCallback, useEffect, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase";
import { useAuth } from "../auth";

const fns = getFunctions(app, "asia-northeast1");

interface Job {
  tenantId: string;
  tenantName: string;
  dishId: string;
  jobId: string;
  type: string | null;
  status: string | null;
  error: string | null;
  createdAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
}

interface Bucket {
  week: string;
  total: number;
  succeeded: number;
  failed: number;
  medianSec: number | null;
  avgSec: number | null;
  measured: number;
}

const STATUS_LABEL: Record<string, string> = {
  queued: "待機中",
  running: "実行中",
  succeeded: "完了",
  failed: "失敗",
};

const FILTERS = [
  { value: "", label: "未完了のみ" },
  { value: "failed", label: "失敗" },
  { value: "running", label: "実行中" },
  { value: "queued", label: "待機中" },
  { value: "succeeded", label: "完了" },
];

function mmss(sec: number | null) {
  if (sec == null) return "—";
  const m = Math.floor(sec / 60);
  return m > 0 ? `${m}分${String(Math.round(sec % 60)).padStart(2, "0")}秒` : `${Math.round(sec)}秒`;
}

export function Ops() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [buckets, setBuckets] = useState<Bucket[] | null>(null);
  const [filter, setFilter] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /** 運用者でなければ Functions が permission-denied を返す。それを画面の出し分けに使う */
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const listJobs = httpsCallable<{ status?: string }, { jobs: Job[]; truncated: boolean }>(
        fns, "opsListJobs",
      );
      const metrics = httpsCallable<{ weeks?: number }, { buckets: Bucket[] }>(fns, "opsMetrics");
      const [j, m] = await Promise.all([
        listJobs(filter ? { status: filter } : {}),
        metrics({ weeks: 8 }),
      ]);
      setJobs(j.data.jobs);
      setBuckets(m.data.buckets);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (/permission-denied|運用者のみ/.test(msg)) setDenied(true);
      else setError(msg);
    }
  }, [filter]);

  useEffect(() => {
    if (user) void load();
  }, [user, load]);

  async function requeue(j: Job) {
    setBusy(j.jobId);
    setError(null);
    try {
      const fn = httpsCallable(fns, "opsRequeueJob");
      await fn({ tenantId: j.tenantId, dishId: j.dishId, jobId: j.jobId });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  if (denied) {
    return (
      <div className="card narrow">
        <h1>運用コンソール</h1>
        <p className="muted">この画面は SAKAMA の運用者のみ利用できます。</p>
      </div>
    );
  }

  return (
    <div className="stack">
      <h1>運用コンソール</h1>
      <p className="muted small">全テナント横断。顧客には見えません。</p>

      {error && <div className="card"><p className="error">{error}</p></div>}

      <div className="card">
        <h2>ジョブ</h2>
        <div className="row">
          <label>
            表示{" "}
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              {FILTERS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </label>
          <button className="link" onClick={() => void load()}>再読み込み</button>
        </div>

        {!jobs ? (
          <p className="muted">読み込み中…</p>
        ) : jobs.length === 0 ? (
          <p className="muted">該当するジョブはありません。</p>
        ) : (
          <div className="tablewrap">
            <table className="matrix">
              <thead>
                <tr>
                  <th>テナント</th><th>種別</th><th>状態</th><th>作成</th><th>エラー</th><th></th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={`${j.tenantId}/${j.dishId}/${j.jobId}`}>
                    <td>{j.tenantName}</td>
                    <td>{j.type ?? "—"}</td>
                    <td>{STATUS_LABEL[j.status ?? ""] ?? j.status ?? "—"}</td>
                    <td className="small">{j.createdAt?.slice(0, 16).replace("T", " ") ?? "—"}</td>
                    <td className="small err-cell">{j.error ?? ""}</td>
                    <td>
                      {j.status === "failed" && (
                        <button disabled={busy === j.jobId} onClick={() => void requeue(j)}>
                          {busy === j.jobId ? "…" : "再キュー"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2>運用計測（直近8週）</h2>
        <p className="muted small">
          Stage B（自動実行）へ投資するかの判断材料。
          <strong>中央値</strong>を見ること（平均は1件の長時間ジョブに引きずられます）。
        </p>
        {!buckets ? (
          <p className="muted">読み込み中…</p>
        ) : buckets.length === 0 ? (
          <p className="muted">対象期間にジョブがありません。</p>
        ) : (
          <div className="tablewrap">
            <table className="matrix">
              <thead>
                <tr>
                  <th>週</th><th>件数</th><th>完了</th><th>失敗</th>
                  <th>所要（中央値）</th><th>所要（平均）</th>
                </tr>
              </thead>
              <tbody>
                {buckets.map((b) => (
                  <tr key={b.week}>
                    <td>{b.week}</td>
                    <td>{b.total}</td>
                    <td>{b.succeeded}</td>
                    <td className={b.failed > 0 ? "err-cell" : ""}>{b.failed}</td>
                    <td>{mmss(b.medianSec)}</td>
                    <td className="muted">{mmss(b.avgSec)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
